import { Helmet } from "@dr.pogodin/react-helmet";
import { Grid, Stack } from "@tine/designsystem-ui-react";
import range from "lodash-es/range";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import Skeleton from "react-loading-skeleton";
import { useLocation } from "wouter";
import type { ContentfulArticle, ContentfulRecipe, ContentfulSection } from "../../common/types/cmsTypes";
import type { RecipeType } from "../../common/types/recipeTypes";
import { initializeWithDefaultData, isDataAvailable, isDefaultData, isWaitingForData } from "../../common/utils/asyncDataUtils";
import contentStore from "../../stores/cms/contentStore";
import theme from "../../themes/theme";
import ContentCardLink from "./ContentCardLink";
import RecipeTeaser from "./recipe/RecipeTeaser";
import SectionNavigator from "./SectionNavigator";

type SectionProps = {
   id: string;
   section: ContentfulSection;
};
const Section = ({ id, section }: SectionProps) => {
   if (!contentStore.sectionContent[id]) {
      runInAction(() => {
         contentStore.sectionContent[id] = initializeWithDefaultData([]);
      });
   }
   const sectionContent = contentStore.sectionContent[id];

   if (isDefaultData(sectionContent)) {
      void contentStore.fetchSectionContentForId(id);
   }

   const [location] = useLocation();
   const urlEntries = location.split("/").filter((entry) => entry !== "");

   const getPageTitle = (): string => {
      switch (urlEntries[0]) {
         case "matfaglig":
            return "Matfaglig inspirasjon";
         case "oppskrifter":
            return "Oppskrifter";
         default:
            return section.fields.title;
      }
   };

   return (
      <Stack gap={6} className="w-full mt-6" align="start">
         <h1 className="text-3xl font-semibold lg:text-4xl">{getPageTitle()}</h1>
         {!!section.fields.title && (
            <Helmet>
               <title>{`${section.fields.title} - ${theme.siteName}`}</title>
            </Helmet>
         )}

         <SectionNavigator />

         {isDataAvailable(sectionContent) && (
            <Grid
               cols={{
                  base: 1,
                  sm: 2,
                  md: 3,
                  lg: 4
               }}
               gap={4}
               className="w-full"
            >
               {sectionContent.data.map((object) => {
                  if (object.sys.contentType.sys.id === "recipe") {
                     const recipeContent = object as ContentfulRecipe;

                     const id = recipeContent.sys.id;
                     const recipe = recipeContent.fields.recipeData as RecipeType;
                     const title = recipeContent.fields.title;
                     if (recipe) {
                        return <RecipeTeaser key={`recipe-teaser-${object.sys.id}`} id={id} recipe={recipe} title={title} />;
                     }
                  } else {
                     const article = object as ContentfulArticle;
                     return (
                        <ContentCardLink
                           entry={article}
                           key={`content-card-link-${article.sys.id}`}
                           trackingListName={"section"}
                        />
                     );
                  }
                  return null;
               })}
            </Grid>
         )}

         {(isDefaultData(sectionContent) || isWaitingForData(sectionContent)) && (
            <Grid cols={4} gap={4}>
               {range(6).map((x) => (
                  <div key={`skeleton${x}`}>
                     <Skeleton width="100%" style={{ paddingBottom: "56.24%" }} />
                  </div>
               ))}
            </Grid>
         )}
      </Stack>
   );
};

export default observer(Section);
